import React from 'react';
import { tokens, cn } from '../../styles/design-tokens';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'title' | 'avatar' | 'button' | 'card' | 'custom';
  width?: string | number;
  height?: string | number;
  count?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className,
  variant = 'text',
  width,
  height,
  count = 1,
}) => {
  const variantStyles: Record<string, string> = {
    text: tokens.skeletonText,
    title: tokens.skeletonTitle,
    avatar: tokens.skeletonAvatar,
    button: tokens.skeletonButton,
    card: tokens.skeletonCard,
    custom: '',
  };

  const style: React.CSSProperties = {};
  if (width) style.width = typeof width === 'number' ? `${width}px` : width;
  if (height) style.height = typeof height === 'number' ? `${height}px` : height;

  const items = Array.from({ length: count }, (_, i) => (
    <div
      key={i}
      className={cn(tokens.skeleton, variantStyles[variant], className)}
      style={style}
    />
  ));

  if (count === 1) return items[0];

  return <div className="space-y-2">{items}</div>;
};

// Table skeleton
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  rows = 5,
  columns = 5,
  showHeader = true,
}) => {
  return (
    <div className="w-full">
      {showHeader && (
        <div className="flex gap-4 p-4 bg-gray-50 border-b border-gray-200 dark:bg-zinc-900 dark:border-zinc-800">
          {Array.from({ length: columns }, (_, i) => (
            <Skeleton key={i} variant="text" className="flex-1" />
          ))}
        </div>
      )}
      {Array.from({ length: rows }, (_, rowIdx) => (
        <div
          key={rowIdx}
          className="flex gap-4 p-4 border-b border-gray-200 dark:border-zinc-800"
        >
          {Array.from({ length: columns }, (_, colIdx) => (
            <Skeleton
              key={colIdx}
              variant="text"
              className="flex-1"
              width={colIdx === 0 ? '60%' : '80%'}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

// Card skeleton
export const CardSkeleton: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={cn(tokens.card, 'p-6', className)}>
      <div className="flex items-start gap-4">
        <Skeleton variant="avatar" />
        <div className="flex-1 space-y-2">
          <Skeleton variant="title" />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="60%" />
        </div>
      </div>
    </div>
  );
};

// KPI Card skeleton
export const KPICardSkeleton: React.FC = () => {
  return (
    <div className={cn(tokens.card, 'p-6')}>
      <div className="flex items-center gap-4">
        <Skeleton variant="custom" className="h-12 w-12 rounded-lg" />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="50%" />
          <Skeleton variant="title" width="70%" />
        </div>
      </div>
    </div>
  );
};

// Stats skeleton (row of KPI cards)
interface SkeletonStatsProps {
  count?: number;
}

export const SkeletonStats: React.FC<SkeletonStatsProps> = ({ count = 4 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: count }, (_, i) => (
        <KPICardSkeleton key={i} />
      ))}
    </div>
  );
};

// Configurable card skeleton
interface SkeletonCardProps {
  className?: string;
  showImage?: boolean;
  lines?: number;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({ 
  className, 
  showImage = true,
  lines = 3 
}) => {
  return (
    <div className={cn(tokens.card, 'p-6', className)}>
      {showImage && (
        <Skeleton variant="custom" className="w-full h-40 rounded-lg mb-4" />
      )}
      <div className="space-y-3">
        <Skeleton variant="title" />
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton key={i} variant="text" width={`${80 - i * 10}%`} />
        ))}
      </div>
    </div>
  );
};

export default Skeleton;
